/*
 * Actions for the [energy_management_system_groups store]{link module:store/energy_management_system_groups}
 *
 * @module store/energy_management_system_groups/actions
 */

import * as apiGroups from '@/api/groups'
import * as apiEmss from '@/api/energy_management_systems'
import { EmsGroup } from '@/store/modules/energy_management_system_groups'

/**
 * Action to reset the emsGroups
 *
 * @function
 *
 */
export function resetStore({ commit }) {
  commit('RESET_EMS_GROUPS')
  return true
}

/**
 * Action to get all OrganizationGroup(s) and the trees under each root
 *
 * @function
 *
 * @param {object} params
 *
 * @return {promise} resolves to the groups (json:api data-response)
 */
export async function getEmsGroupsAndRoots({ commit }) {
  const filter = {}
  const rsp = await apiGroups.getGroups({
    filter,
    fields: ['name', 'display_name', 'parent_id'],
    sort: 'id'
  })
  const groups = rsp.payload.data

  const groupIds = []
  const parentIds = []
  const rootIds = []

  groups.forEach((g) => {
    const id = parseInt(g.id)
    groupIds.push(id)
    if (g.attributes.parentId) {
      parentIds.push(g.attributes.parentId)
    } else {
      rootIds.push(id)
    }

    commit(
      'SET_EMS_GROUP',
      new EmsGroup({
        id: g.id,
        name: g.attributes.name,
        displayName: g.attributes.displayName,
        parentId: g.attributes.parentId
      })
    )
  })

  // find "local" root IDs
  parentIds
    .filter((pId) => {
      return !groupIds.includes(pId)
    })
    .forEach((pId) => {
      groups.forEach((g) => {
        if (g.attributes.parentId === pId) {
          rootIds.push(parseInt(g.id))
        }
      })
    })

  const fullTree = {}
  for (const rId of rootIds) {
    const rsp = await apiGroups.getHierarchy({ id: rId })
    const tree = rsp.payload.data

    Object.assign(fullTree, {
      [tree.id]: parseToHierarchy(tree.attributes.children)
    })
  }

  commit('SET_EMS_GROUPS_HIERARCHY', fullTree)

  return groups
}

/**
 * Action to get the trees for a group
 *
 * @function
 *
 * @param {object} params
 *
 * @return {promise} resolves to the groups (json:api data-response)
 */
export async function getGroupHierarchy({ commit }, { rootId }) {
  const groupTree = {}
  const rsp = await apiGroups.getHierarchy({ id: rootId })
  const tree = rsp.payload.data

  Object.assign(groupTree, {
    [tree.id]: parseToHierarchy(tree.attributes.children)
  })

  commit('SET_EMS_GROUPS_HIERARCHY', groupTree)
  return groupTree
}

/**
 * Action to get desired/all EmsGroup(s)
 *
 * Gets all available EmsGroup(s) for empty emsGroupIds.
 *
 * @function
 *
 * @param {object} params
 * @param {array} params.emsGroupIds is a list of EmsGroup IDs, to be filteted by.
 * @param {array} params.type is the GroupType to be filteted by.
 *
 * @return {promise} resolves to the groups (json:api data-response)
 */
export async function getEmsGroups({ commit }, { ctxt, emsGroupIds = [], type = undefined } = {}) {
  const filter = {}
  if (emsGroupIds && emsGroupIds.length > 0) {
    filter.id = emsGroupIds.join(',')
  }
  const rsp = await apiGroups.getGroups({
    ctxt,
    filter,
    fields: ['name', 'display_name', 'parent_id', 'tag'],
    sort: 'id',
    type
  })
  const groups = rsp.payload.data

  groups.forEach((g) => {
    commit(
      'SET_EMS_GROUP',
      new EmsGroup({
        id: g.id,
        name: g.attributes.name,
        displayName: g.attributes.displayName,
        parentId: g.attributes.parentId,
        tag: g.attributes.tag
      })
    )
  })

  return groups
}

/**
 * Action to get one EmsGroup by ID.
 *
 * @function
 * @param {object} params
 * @param {array} params.emsGroupId (required) is the EmsGroup ID
 *
 * @return {promise} resolves to the group (json:api data-response)
 */
export async function getEmsGroup({ commit }, { emsGroupId } = {}) {
  const rsp = await apiGroups.getGroup({
    id: emsGroupId,
    fields: ['name', 'display_name', 'parent_id']
  })
  const group = rsp.payload.data
  // eslint-disable-next-line eqeqeq
  if (group.id != emsGroupId) {
    throw new Error(`Unexpected server response. Requested ${emsGroupId}, but server responded with ${rsp.data.id}.`)
  }

  commit(
    'SET_EMS_GROUP',
    new EmsGroup({
      id: group.id,
      name: group.attributes.name,
      displayName: group.attributes.displayName,
      parentId: group.attributes.parentId
    })
  )

  return group
}

/**
 * Action to get the All EmsGroups related to a given EMS
 *
 * Data is currently mocked above. Will be requested through an AJAX request.
 *
 * Sets the corresponding EmsGroups in state.emsGroups.hash
 *
 * Sets the related emsGroupIds in state.emss[emsId]
 *
 * @function
 *
 * @params {Integer} emsId of the desired EMS
 *
 * @return Array{Integer} the energy_management_system_group_ids
 */
export async function getGroupsForEms(
  { commit, dispatch },
  { emsId, ctxt = 'admin', membership = 'Groups::OrganizationGroup' }
) {
  const rsp = await apiEmss.getGroupsForEms({ id: emsId, ctxt, membership })

  var emsGroupIds = rsp.payload.data.map((x) => {
    return parseInt(x.id)
  })

  await dispatch('energyManagementSystemGroups/getEmsGroups', { emsGroupIds, ctxt }, { root: true })

  commit('ADD_GROUPS_FOR_EMS', { emsId, emsGroupIds })

  return emsGroupIds
}

/**
 * Helper function to parse a JSON-API dataset of emsGroups to create a dependancy tree
 *
 * Sets the EmsGroups tree in state.emsGroups.hierarchy
 *
 * @function
 *
 * @return {object} emsGroupIds as Tree
 */
function parseToHierarchy(emsGroups) {
  var hierarchy = {}

  for (var emsGroup of emsGroups) {
    if (emsGroup.attributes.children) {
      hierarchy[emsGroup.id] = parseToHierarchy(emsGroup.attributes.children)
    } else {
      hierarchy[emsGroup.id] = {}
    }
  }

  return hierarchy
}

/**
 * Action to get the All Community groups
 *
 * Sets the corresponding EmsGroups in state.emsGroups.hash
 *
 * @function
 *
 * @params {String} ctxt of the call
 *
 * @return {Integer[]} the community_group_ids
 */
export async function getCommunityGroupIds({ commit }, { ctxt = 'customer' }) {
  const rsp = await apiGroups.getGroups({ ctxt, type: 'Groups::CommunityGroup', parentId: null })

  const data = rsp.payload.data
  const groupIds = []
  for (const g of data) {
    groupIds.push(g.id)
    commit(
      'SET_EMS_GROUP',
      new EmsGroup({
        id: g.id,
        name: g.attributes.name
      })
    )
  }
  return groupIds
}

/**
 * Action to update a Group
 *
 * sends a PATCH API call and saves the change in the store
 *
 * @function
 *
 * @params {String} ctxt of the call
 *
 * @params {integer} id the group ID
 *
 * @params {String} name potential new group name
 *
 * @params {String} tag potential new tag 'TRAFO' | 'RESIDENCE'
 *
 * @return {Promise}
 */
export async function updateGroup({ commit }, { ctxt = 'admin', id = null, name = undefined, tag = undefined }) {
  if (!id) {
    return
  }
  return apiGroups.updateGroup({ ctxt, id, name, tag }).then((rsp) => {
    const group = rsp.payload.data
    commit(
      'SET_EMS_GROUP',
      new EmsGroup({
        id: group.id,
        name: group.attributes.name,
        displayName: group.attributes.displayName,
        parentId: group.attributes.parentId,
        tag: group.attributes.tag
      })
    )
  })
}

/**
 * Action to create a Group
 *
 * sends a CREATE API call and saves the new Group in the store afterwards
 *
 * @function
 *
 * @params {String} ctxt of the call
 *
 * @params {integer} id the group ID
 *
 * @params {String} name, new group name
 *
 * @params {String} tag, new tag 'TRAFO' | 'RESIDENCE'
 *
 * @params {String} type, new group type 'Groups::OrganizationGroup' | 'Groups::CommunityGroup'
 *
 * @return {Promise}
 */
export async function createGroup(
  { commit },
  { ctxt = 'admin', id = undefined, name = undefined, tag = undefined, type = undefined }
) {
  return apiGroups
    .createGroup({
      ctxt,
      id,
      name,
      tag,
      type
    })
    .then((rsp) => {
      const group = rsp.payload.data
      commit(
        'SET_EMS_GROUP',
        new EmsGroup({
          id: group.id,
          name: group.attributes.name,
          displayName: group.attributes.displayName,
          parentId: group.attributes.parentId,
          tag: group.attributes.tag
        })
      )
      const parentId = group.attributes.parentId
      if (parentId) {
        commit('ADD_GROUP_TO_HIERARCHY', { parentId: group.attributes.parentId, groupId: group.id })
      }

      return group
    })
}

/**
 * Action to delete a Group
 *
 * sends a DELETE API call and removes it from the store
 *
 * @function
 *
 * @params {String} ctxt of the call
 *
 * @params {integer} id the group ID
 *
 * @params {String} memberType, safe memberType 'users' | 'energyManagementSystems'
 *
 * @return {Promise}
 */
export async function deleteGroup({ commit }, { ctxt = 'admin', id = undefined, memberType = undefined }) {
  return apiGroups
    .deleteGroup({
      ctxt,
      id,
      memberType
    })
    .then(() => {
      commit('REMOVE_GROUP', { id })
    })
}
